/* global MediaStream */
import { Record, Map } from 'immutable';
import { ITrackMids } from '@engoo/realtime-protocol/signaling/negotiation';

export interface IStreams {
  audio?: MediaStream;
  video?: MediaStream;
  screen?: MediaStream;
}

const FALLBACK_STREAM_MIDS: { [K in keyof Pick<Required<ITrackMids>, 'audio' | 'video'>]: string } = {
  audio: 'FALLBACK_AUDIO_MID',
  video: 'FALLBACK_VIDEO_MID',
};

export const getFallbackMid = ({ kind }: MediaStreamTrack) => {
  return FALLBACK_STREAM_MIDS[kind === 'audio' ? 'audio' : 'video'];
};

type TrackList = ReadonlyArray<MediaStreamTrack | undefined>;

const mergeTracksToStream = ({ tracks, stream, keepTracks = [] }: {
  tracks: TrackList,
  keepTracks?: TrackList,
  stream?: MediaStream,
}) => {
  const filteredTracks = tracks.filter((track): track is MediaStreamTrack => Boolean(track));

  if (filteredTracks.length === 0) {
    return undefined;
  }

  if (!stream) {
    return new MediaStream(filteredTracks);
  }

  // Remove tracks from stream that are no longer in set of tracks
  // to use
  for (const track of stream.getTracks()) {
    if (![...tracks, ...keepTracks].find((t) => track.id === t?.id)) {
      stream.removeTrack(track);
    }
  }

  // Add all tracks not already in the stream
  for (const track of filteredTracks) {
    if (!stream.getTrackById(track.id)) {
      stream.addTrack(track);
    }
  }

  return stream;
};

const getTrack = (tracks: Map<string, MediaStreamTrack>, mid: string | null | undefined, fallbackMid?: string) => {
  const track = mid && tracks.get(mid);

  if (track) {
    return track;
  } else if (fallbackMid) {
    return tracks.get(fallbackMid);
  }
};

export default class Streams extends Record<IStreams>({
  audio: undefined,
  video: undefined,
  screen: undefined,
}) {
  public mergeTracks(mids: ITrackMids, tracks: Map<string, MediaStreamTrack>) {
    const screenAudioTrack = getTrack(tracks, mids.screenAudio);
    const screenVideoTrack = getTrack(tracks, mids.screenVideo);

    let audioTrack = getTrack(tracks, mids.audio, FALLBACK_STREAM_MIDS.audio);
    let videoTrack = getTrack(tracks, mids.video, FALLBACK_STREAM_MIDS.video);

    if (audioTrack === screenAudioTrack) {
      audioTrack = undefined;
    }

    if (videoTrack === screenVideoTrack) {
      videoTrack = undefined;
    }

    return this.merge({
      audio: mergeTracksToStream({
        tracks:     [audioTrack],
        keepTracks: [videoTrack],
        stream:     this.audio,
      }),
      video: mergeTracksToStream({
        tracks:     [videoTrack],
        keepTracks: [audioTrack],
        stream:     this.video,
      }),
      screen: mergeTracksToStream({
        tracks: [
          screenAudioTrack,
          screenVideoTrack,
        ],
        stream: this.screen,
      }),
    });
  }

  public mergeStream(stream: MediaStream) {
    return this
      .update('audio', (old) => stream.getAudioTracks().length > 0 ? stream : old)
      .update('video', (old) => stream.getVideoTracks().length > 0 ? stream : old);
  }
}
